/**
 * Constantes e configurações para o sistema de Pixels
 * 
 * Este arquivo centraliza todas as configurações relacionadas aos pixels,
 * facilitando manutenção e evitando hardcoding de valores sensíveis.
 */

/**
 * Pixel ID da Sellyo usado para disparar PageView
 * Necessário para que os cookies do Facebook sejam identificados corretamente
 * devido às atualizações do iOS 14+
 */
export const SELLYO_FACEBOOK_PIXEL_ID = '840935265038640';
// O token da API de Conversões da Sellyo fica SÓ no backend (SELLYO_FB_CAPI_TOKEN).
// No navegador ele não tem função nenhuma — e ficava visível no JavaScript do checkout.

/**
 * Domínio padrão da Sellyo para disparo de pixels
 * Este domínio é usado quando o usuário não configura um domínio personalizado
 */
export const SELLYO_DEFAULT_DOMAIN = 'sellyo.com.br';

/**
 * Subdomínio de tracking da Sellyo
 * Formato: tracking.seusite.com -> CNAME -> tracking.sellyo.com.br
 */
export const SELLYO_TRACKING_SUBDOMAIN = 'tracking';
export const SELLYO_TRACKING_DOMAIN = 'tracking.sellyo.com.br';

/**
 * Configurações padrão para porcentagem de valor em PIX/Boleto
 */
export const DEFAULT_PIX_PERCENTAGE = 100; // 100% do valor
export const DEFAULT_BOLETO_PERCENTAGE = 100; // 100% do valor

/**
 * Eventos suportados pelo sistema de pixels (Meta/Facebook)
 * Nomes canônicos seguindo padrão Meta Pixel
 */
export const PIXEL_EVENTS = {
    PAGE_VIEW: 'PageView',
    VIEW_CONTENT: 'ViewContent',
    INITIATE_CHECKOUT: 'InitiateCheckout',
    ADD_PAYMENT_INFO: 'AddPaymentInfo',
    PURCHASE: 'Purchase',
    PIX: 'pix',
    BOLETO: 'boleto',
    CREDIT_CARD: 'creditCard',
} as const;

/**
 * Provedores de pixel suportados
 */
export const PIXEL_PROVIDERS = {
    FACEBOOK: 'facebook',
    GOOGLE: 'google',
    TIKTOK: 'tiktok',
} as const;

/**
 * Status de pixels
 */
export const PIXEL_STATUS = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
} as const;

/**
 * Configurações de timeout para scripts de pixel
 */
export const PIXEL_SCRIPT_TIMEOUT = 10000; // 10 segundos

/**
 * Configurações de retry para falhas de disparo
 */
export const PIXEL_RETRY_CONFIG = {
    MAX_RETRIES: 3,
    RETRY_DELAY: 1000, // 1 segundo
    BACKOFF_MULTIPLIER: 2, // Exponential backoff
};

/**
 * Validação de domínio - Regex para validar formato de domínio
 */
export const DOMAIN_REGEX = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/i;

/**
 * Helper para validar domínio
 */
export const isValidDomain = (domain: string): boolean => {
    return DOMAIN_REGEX.test(domain);
};

/**
 * Helper para construir URL de tracking
 */
export const buildTrackingUrl = (userDomain: string): string => {
    return `${SELLYO_TRACKING_SUBDOMAIN}.${userDomain}`;
};

/**
 * Query parameters e headers para tracking first-party
 */
export const FBCLID_PARAM = 'fbclid';
export const EVENT_ID_HEADER = 'x-sellyo-event-id';
export const EVENT_ID_STORAGE_PREFIX = 'sellyo:event:';

/**
 * Endpoints do servidor de tracking first-party
 * Backend: app.use('/track', trackingRoutes)
 */
export const TRACKING_ENDPOINTS = {
    LOADER_SCRIPT: '/track/p.js',
    COLLECT: '/track/collect',
    CAPI: '/track/capi',
} as const;

/**
 * Configurações de cookies first-party
 */
export const COOKIE_CONFIG = {
    FBP_NAME: '_fbp',
    FBC_NAME: '_fbc',
    FBP_EXPIRY_DAYS: 90,
    FBC_EXPIRY_DAYS: 90,
    DOMAIN_PREFIX: '.',
} as const;
